/**
 * The pieces every /ndt-inspection screen draws the same way.
 *
 * The panel, dialog, field and input idiom are the downhole screens' own
 * (pages/downhole/DownholeBits.tsx), IMPORTED rather than copied, as Pressure
 * Testing does — so the verticals stay one shape. Only what is NDT's own lives
 * here: the status badge, the filter chip, the register table and the form
 * error line.
 */
import React from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { formatCalendarDate } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import type { NdtSkin } from '../../lib/ndt-inspection';

export {
  DhDialog as NdtDialog,
  DhEmpty as NdtEmpty,
  DhField as NdtField,
  DhPanel as NdtPanel,
  DhSpec as NdtSpec,
  DH_INPUT as NDT_INPUT,
  DH_DATE_INPUT as NDT_DATE_INPUT,
  DH_SECONDARY as NDT_SECONDARY,
  useOpenCount,
} from '../downhole/DownholeBits';

export const NDT_PRIMARY = 'fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-60';

/** A calendar day (YYYY-MM-DD) as words; absent → `—`. Never `new Date('YYYY-MM-DD')`. */
export const ndtDay = (value: string | null | undefined) => formatCalendarDate(value) ?? '—';

/** A status as a saturated badge, dark ink on the fill. The skin decides label and hue. */
export function NdtBadge({ skin, value }: { skin: NdtSkin; value?: string | null }) {
  return (
    <span
      data-status={value ?? ''}
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/** One filter pill: amber-filled when active, its count beside it (none while loading). */
export function NdtChip({
  label,
  active,
  count,
  onClick,
}: {
  label: string;
  active: boolean;
  count?: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
        active
          ? 'bg-fi-amber text-fi-amber-ink'
          : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
      }`}
    >
      {label}
      {count !== undefined && (
        <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{count}</span>
      )}
    </button>
  );
}

export function NdtChipRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label={label}>
      {children}
    </div>
  );
}

export function NdtFormError({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <p role="alert" className="rounded-fi-control border border-fi-signal-red/40 bg-fi-signal-red/10 px-3 py-2 text-sm text-fieldiq-text">
      {message}
    </p>
  );
}

export function NdtLoading({ label }: { label: string }) {
  return (
    <div className="flex justify-center py-16">
      <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label={label} />
    </div>
  );
}

export interface NdtColumn<T> {
  key: string;
  header: string;
  render: (row: T) => React.ReactNode;
  align?: 'right';
}

/**
 * A register: the panel's table from `md`, stacked cards below it. Every row
 * is one keyboard target opening the record's own page (no inline buttons, so
 * the row may carry role="button"). The rail is the status colour; an unknown
 * status keeps the 4px width and claims no colour.
 *
 * Three states, never two (CLAUDE.md): loading, a failure in the place the
 * empty state would be, a genuinely empty list worded as a fact, and rows.
 */
export function NdtRegister<T extends { id: string }>({
  query,
  rows,
  columns,
  rail,
  onOpen,
  card,
  subject,
  empty,
  testId,
  minWidth = 'min-w-[880px]',
}: {
  query: { isLoading: boolean; error?: unknown; failureReason?: unknown; fetchStatus?: string };
  rows: T[];
  columns: NdtColumn<T>[];
  rail: (row: T) => string;
  /** Omit for a read-only register: rows are then not targets and carry no chevron. */
  onOpen?: (row: T) => void;
  card: (row: T) => React.ReactNode;
  subject: string;
  empty: React.ReactNode;
  testId: string;
  minWidth?: string;
}) {
  const failure = queryLoadFailure(query);
  if (query.isLoading) return <NdtLoading label={`Loading ${subject}`} />;
  if (failure) return <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject={subject} />;
  if (rows.length === 0) return <>{empty}</>;
  const keys = (row: T) => (event: React.KeyboardEvent) => {
    if (onOpen && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onOpen(row);
    }
  };
  const target = (row: T) =>
    onOpen ? { role: 'button' as const, tabIndex: 0, onClick: () => onOpen(row), onKeyDown: keys(row) } : {};
  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className={`w-full ${minWidth} text-left`}>
          <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
            <tr className="border-l-4 border-l-transparent">
              {columns.map((c) => (
                <th key={c.key} scope="col" className={`px-4 py-3 ${c.align === 'right' ? 'text-right' : ''}`}>
                  {c.header}
                </th>
              ))}
              {onOpen && <th scope="col" className="w-[44px] px-4 py-3" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                {...target(row)}
                data-testid={`${testId}-row`}
                className={`${onOpen ? 'cursor-pointer' : ''} border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${rail(row)}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className={`px-4 py-3 text-sm text-fieldiq-text ${c.align === 'right' ? 'text-right font-mono tabular-nums' : ''}`}>
                    {c.render(row)}
                  </td>
                ))}
                {onOpen && (
                  <td className="px-4 py-3">
                    <ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-3 p-4 md:hidden">
        {rows.map((row) => (
          <div
            key={row.id}
            {...target(row)}
            className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
          >
            {card(row)}
          </div>
        ))}
      </div>
    </>
  );
}

/** A record's page could not be read: 404 is an answer about the record, anything else a failure. */
export function isNotFound(error: unknown): boolean {
  return (error as { response?: { status?: number } } | null)?.response?.status === 404;
}
